import { useEffect, useState, useMemo } from "react";
import { api, formatTRY, errMsg } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Plus, Trash2, Users, Edit3, ArrowUp, ArrowDown, Search } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

export default function Employees() {
  const { user } = useAuth();
  const canEdit = user?.role === "admin" || user?.role === "partner";
  const [companies, setCompanies] = useState([]);
  const [filter, setFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState({
    company_id: "", name: "", position: "",
    iban: "", salary_official: "", salary_informal: "",
  });

  const load = async () => {
    const [c, e] = await Promise.all([
      api.get("/companies"),
      api.get("/employees"),
    ]);
    setCompanies(c.data); setItems(e.data);
  };
  useEffect(() => { load(); }, []);

  const resetForm = () => {
    setEditId(null);
    setForm({ company_id:"", name:"", position:"", iban:"", salary_official:"", salary_informal:"" });
  };

  const openCreate = () => { resetForm(); setOpen(true); };
  const openEdit = (e) => {
    setEditId(e.id);
    setForm({
      company_id: e.company_id,
      name: e.name,
      position: e.position,
      iban: e.iban || "",
      salary_official: String(e.salary_official ?? ""),
      salary_informal: String(e.salary_informal ?? ""),
    });
    setOpen(true);
  };

  const submit = async (ev) => {
    ev.preventDefault();
    try {
      const payload = {
        ...form,
        salary_official: Number(form.salary_official || 0),
        salary_informal: Number(form.salary_informal || 0),
      };
      if (editId) {
        await api.put(`/employees/${editId}`, payload);
        toast.success("Denetçi güncellendi");
      } else {
        await api.post("/employees", payload);
        toast.success("Denetçi eklendi");
      }
      setOpen(false);
      resetForm();
      load();
    } catch (ex) { toast.error(errMsg(ex)); }
  };

  const remove = async (id) => {
    if (!window.confirm("Denetçi silinsin mi?")) return;
    await api.delete(`/employees/${id}`);
    toast.success("Silindi");
    load();
  };

  const move = async (id, direction) => {
    try {
      await api.put(`/employees/${id}/move`, null, { params: { direction } });
      load();
    } catch (ex) { toast.error(errMsg(ex)); }
  };

  const companyName = (id) => companies.find(c => c.id === id)?.name || "—";

  const filtered = useMemo(() => {
    const q = search.trim().toLocaleLowerCase("tr");
    return items.filter((i) => {
      if (filter !== "all" && i.company_id !== filter) return false;
      if (!q) return true;
      return (
        (i.name || "").toLocaleLowerCase("tr").includes(q) ||
        (i.position || "").toLocaleLowerCase("tr").includes(q) ||
        (i.iban || "").toLocaleLowerCase("tr").includes(q) ||
        companyName(i.company_id).toLocaleLowerCase("tr").includes(q)
      );
    });
  }, [items, filter, search, companies]);

  const totals = filtered.reduce(
    (s, e) => ({
      official: s.official + Number(e.salary_official || 0),
      informal: s.informal + Number(e.salary_informal || 0),
    }),
    { official: 0, informal: 0 }
  );

  return (
    <div className="space-y-6" data-testid="employees-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="text-[10px] font-mono uppercase tracking-[.25em] text-slate-500">Şirket Denetçileri</div>
          <h1 className="text-3xl font-bold tracking-tight fk-heading">Şirket Denetçileri Yönetimi</h1>
          <p className="text-sm text-slate-400 mt-1">Her denetçinin IBAN'ı ve resmi/normal maaş dağılımı takip edilir.</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-500 pointer-events-none" />
            <Input data-testid="emp-search" value={search} onChange={(e)=>setSearch(e.target.value)}
                   placeholder="Ara: isim, pozisyon, IBAN…"
                   className="w-[260px] pl-9 bg-slate-900/60 border-slate-700" />
          </div>
          <Select value={filter} onValueChange={setFilter}>
            <SelectTrigger data-testid="emp-filter" className="w-[180px] bg-slate-900/60 border-slate-700">
              <SelectValue placeholder="Firma Filtrele"/>
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tüm Firmalar</SelectItem>
              {companies.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
            </SelectContent>
          </Select>
          {canEdit && (
            <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) resetForm(); }}>
              <DialogTrigger asChild>
                <Button data-testid="add-emp-btn" onClick={openCreate} className="bg-indigo-600 hover:bg-indigo-500">
                  <Plus className="w-4 h-4 mr-1.5"/> Yeni Denetçi
                </Button>
              </DialogTrigger>
              <DialogContent className="bg-[#131b2e] border-white/10 max-w-lg">
                <DialogHeader><DialogTitle>{editId ? "Denetçi Düzenle" : "Yeni Denetçi"}</DialogTitle></DialogHeader>
                <form onSubmit={submit} className="space-y-4">
                  <div className="space-y-2">
                    <Label>Firma</Label>
                    <Select value={form.company_id} onValueChange={(v)=>setForm({...form, company_id:v})}>
                      <SelectTrigger data-testid="emp-company" className="bg-slate-900/60 border-slate-700">
                        <SelectValue placeholder="Seçin"/>
                      </SelectTrigger>
                      <SelectContent>
                        {companies.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-2">
                      <Label>Ad Soyad</Label>
                      <Input required data-testid="emp-name" value={form.name}
                             onChange={(e)=>setForm({...form, name:e.target.value})}
                             className="bg-slate-900/60 border-slate-700"/>
                    </div>
                    <div className="space-y-2">
                      <Label>Pozisyon / Uzmanlık</Label>
                      <Input required data-testid="emp-position" value={form.position}
                             onChange={(e)=>setForm({...form, position:e.target.value})}
                             className="bg-slate-900/60 border-slate-700"/>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label>IBAN</Label>
                    <Input data-testid="emp-iban" value={form.iban}
                           onChange={(e)=>setForm({...form, iban:e.target.value})}
                           placeholder="TR00 0000 0000 0000 0000 0000 00"
                           className="bg-slate-900/60 border-slate-700 fk-mono"/>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-2">
                      <Label>Resmi (₺)</Label>
                      <Input type="number" step="0.01" data-testid="emp-salary-official" value={form.salary_official}
                             onChange={(e)=>setForm({...form, salary_official:e.target.value})}
                             className="bg-slate-900/60 border-slate-700"/>
                    </div>
                    <div className="space-y-2">
                      <Label>Normal (₺)</Label>
                      <Input type="number" step="0.01" data-testid="emp-salary-informal" value={form.salary_informal}
                             onChange={(e)=>setForm({...form, salary_informal:e.target.value})}
                             className="bg-slate-900/60 border-slate-700"/>
                    </div>
                  </div>
                  <DialogFooter>
                    <Button type="submit" data-testid="emp-submit" className="bg-indigo-600 hover:bg-indigo-500">
                      {editId ? "Güncelle" : "Kaydet"}
                    </Button>
                  </DialogFooter>
                </form>
              </DialogContent>
            </Dialog>
          )}
        </div>
      </div>

      <div className="fk-card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-white/[.02] text-slate-400 text-xs uppercase tracking-wider">
              <tr>
                {canEdit && <th className="w-14"></th>}
                <th className="text-left px-5 py-3 font-medium">Denetçi</th>
                <th className="text-left px-5 py-3 font-medium">Firma</th>
                <th className="text-left px-5 py-3 font-medium">Pozisyon</th>
                <th className="text-left px-5 py-3 font-medium">IBAN</th>
                <th className="text-right px-5 py-3 font-medium">Resmi</th>
                <th className="text-right px-5 py-3 font-medium">Normal</th>
                {canEdit && <th className="w-24"></th>}
              </tr>
            </thead>
            <tbody className="divide-y divide-white/5">
              {filtered.map((e, i) => (
                <tr key={e.id} data-testid={`emp-row-${e.id}`} className="hover:bg-white/[.02]">
                  {canEdit && (
                    <td className="px-2 py-3">
                      <div className="flex flex-col items-center gap-0.5">
                        <Button variant="ghost" size="sm" onClick={()=>move(e.id, "up")}
                                data-testid={`move-up-${e.id}`}
                                disabled={i === 0 && filter === "all" && !search}
                                className="h-6 w-6 p-0 text-slate-500 hover:text-indigo-300 hover:bg-indigo-500/10 disabled:opacity-30">
                          <ArrowUp className="w-3.5 h-3.5"/>
                        </Button>
                        <Button variant="ghost" size="sm" onClick={()=>move(e.id, "down")}
                                data-testid={`move-down-${e.id}`}
                                disabled={i === filtered.length - 1 && filter === "all" && !search}
                                className="h-6 w-6 p-0 text-slate-500 hover:text-indigo-300 hover:bg-indigo-500/10 disabled:opacity-30">
                          <ArrowDown className="w-3.5 h-3.5"/>
                        </Button>
                      </div>
                    </td>
                  )}
                  <td className="px-5 py-3">
                    <div className="flex items-center gap-2.5">
                      <div className="w-8 h-8 rounded-full bg-slate-500/15 border border-slate-500/30 flex items-center justify-center">
                        <Users className="w-4 h-4 text-slate-300"/>
                      </div>
                      <span className="font-medium text-white">{e.name}</span>
                    </div>
                  </td>
                  <td className="px-5 py-3">
                    <span className="fk-pill bg-indigo-500/10 text-indigo-300 border border-indigo-500/25">
                      {companyName(e.company_id)}
                    </span>
                  </td>
                  <td className="px-5 py-3 text-slate-400">{e.position}</td>
                  <td className="px-5 py-3 fk-mono text-slate-300 text-xs max-w-[220px] truncate">{e.iban || "—"}</td>
                  <td className="px-5 py-3 text-right fk-mono text-emerald-300">{formatTRY(e.salary_official || 0)}</td>
                  <td className="px-5 py-3 text-right fk-mono text-amber-300">{formatTRY(e.salary_informal || 0)}</td>
                  {canEdit && (
                    <td className="px-5 py-3 text-right">
                      <div className="inline-flex items-center gap-1">
                        <Button variant="ghost" size="sm" onClick={()=>openEdit(e)}
                                data-testid={`edit-emp-${e.id}`}
                                className="text-slate-500 hover:text-indigo-300 hover:bg-indigo-500/10 h-8 w-8 p-0">
                          <Edit3 className="w-4 h-4"/>
                        </Button>
                        <Button variant="ghost" size="sm" onClick={()=>remove(e.id)}
                                data-testid={`del-emp-${e.id}`}
                                className="text-slate-500 hover:text-rose-400 hover:bg-rose-500/10 h-8 w-8 p-0">
                          <Trash2 className="w-4 h-4"/>
                        </Button>
                      </div>
                    </td>
                  )}
                </tr>
              ))}
              {filtered.length === 0 && (
                <tr><td colSpan={canEdit ? 8 : 6} className="px-5 py-10 text-center text-slate-500">
                  {search ? `"${search}" için denetçi bulunamadı` : "Denetçi yok."}
                </td></tr>
              )}
            </tbody>
            {filtered.length > 0 && (
              <tfoot className="bg-white/[.03] font-semibold border-t border-white/10">
                <tr>
                  {canEdit && <td></td>}
                  <td className="px-5 py-3 text-slate-300 uppercase text-xs tracking-wider" colSpan={4}>Toplam ({filtered.length})</td>
                  <td className="px-5 py-3 text-right fk-mono text-emerald-300" data-testid="total-official">{formatTRY(totals.official)}</td>
                  <td className="px-5 py-3 text-right fk-mono text-amber-300" data-testid="total-informal">{formatTRY(totals.informal)}</td>
                  {canEdit && <td></td>}
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </div>
    </div>
  );
}
